import React, { useState, useEffect, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { WriteOnly } from '../components/ViewerGuard';
import { formatApiError } from '../lib/api';
import api from '../lib/api';
import usePageTitle from '../hooks/usePageTitle';
import {
  Save, Loader2, Plus, X, Brain, Zap, MessageSquare,
  Target, Eye, ChevronDown, ChevronUp, Upload, Sparkles,
  Building2, Settings, Shield
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const inputCls = "w-full p-2.5 rounded-lg bg-[#0A1628] border border-[#1A2F4C] text-[#D4E2F0] text-sm focus:border-[#00D4C8] focus:ring-1 focus:ring-[#00D4C8]/20 placeholder:text-[#A3B5C7]/40";
const labelCls = "block text-[10px] uppercase tracking-wider text-[#A3B5C7] mb-1 font-medium";

const settingsTabCls = ({ isActive }) =>
  `px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
    isActive ? 'bg-[#00D4C8]/10 text-[#00D4C8]' : 'text-[#A3B5C7] hover:text-white hover:bg-[#0A1628]'
  }`;

const SettingsNav = () => {
  const { isAdmin } = useAuth();
  return (
    <div className="flex gap-1 mb-6 bg-[#0E1C33] p-1 rounded-lg w-fit" data-testid="settings-nav">
      <NavLink to="/settings/org-profile" className={settingsTabCls}>
        <Building2 className="w-3.5 h-3.5" /> Organisation Profile
      </NavLink>
      <NavLink to="/settings/thinking-model" className={settingsTabCls}>
        <Brain className="w-3.5 h-3.5" /> Thinking Model
      </NavLink>
      {isAdmin && (
        <NavLink to="/settings/admin" className={settingsTabCls} data-testid="settings-nav-admin">
          <Shield className="w-3.5 h-3.5" /> Admin
        </NavLink>
      )}
    </div>
  );
};

export { SettingsNav };

const SectionHeader = ({ icon: Icon, title, subtitle, collapsed, onToggle }) => (
  <button onClick={onToggle} className="w-full flex items-center justify-between py-3 group">
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-[#00D4C8]/10 flex items-center justify-center">
        <Icon className="w-4 h-4 text-[#00D4C8]" />
      </div>
      <div className="text-left">
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        {subtitle && <p className="text-[10px] text-[#A3B5C7]">{subtitle}</p>}
      </div>
    </div>
    {collapsed ? <ChevronDown className="w-4 h-4 text-[#A3B5C7]" /> : <ChevronUp className="w-4 h-4 text-[#A3B5C7]" />}
  </button>
);

const ListEditor = ({ items, onChange, placeholder }) => {
  const [newItem, setNewItem] = useState('');
  const addItem = () => {
    if (newItem.trim()) {
      onChange([...items, newItem.trim()]);
      setNewItem('');
    }
  };
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="flex-1 text-sm text-[#D4E2F0] bg-[#0A1628] px-3 py-1.5 rounded-lg border border-[#1A2F4C]">{item}</span>
          <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-red-400/50 hover:text-red-400 p-1"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <input className={inputCls} value={newItem} onChange={e => setNewItem(e.target.value)} placeholder={placeholder} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addItem())} />
        <button onClick={addItem} className="btn-secondary text-xs px-3 flex items-center gap-1" type="button"><Plus className="w-3 h-3" /> Add</button>
      </div>
    </div>
  );
};

const CognitiveProfilePage = () => {
  usePageTitle('Thinking Model');
  const { isTester } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanText, setScanText] = useState('');
  const [scanDraft, setScanDraft] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  const toggle = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  const loadProfile = useCallback(async () => {
    try {
      const res = await api.get('/cognitive-profile');
      setProfile(res.data);
    } catch (err) {
      console.error('Failed to load cognitive profile:', err);
      setProfile({ is_calibrated: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/cognitive-profile', profile);
      setProfile(res.data);
      toast.success('Thinking Model saved');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleScan = async () => {
    if (scanText.trim().length < 200) {
      toast.error('Please paste a longer conversation export');
      return;
    }
    setScanning(true);
    try {
      const res = await api.post('/cognitive-profile/scan', { raw_text: scanText });
      if (res.data.error) {
        toast.error(res.data.error);
      } else if (res.data.profile) {
        setScanDraft(res.data.profile);
        toast.success('Thinking Model draft generated — review and save');
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setScanning(false);
    }
  };

  const applyDraft = () => {
    setProfile(prev => ({ ...prev, ...scanDraft }));
    setScanDraft(null);
    setScanText('');
    toast.info('Draft applied — review and click Save to confirm');
  };

  const update = (field, value) => setProfile(prev => ({ ...prev, [field]: value }));
  const updatePref = (key, value) => setProfile(prev => ({
    ...prev,
    output_preferences: { ...(prev.output_preferences || {}), [key]: value }
  }));

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-[#00D4C8]" />
    </div>
  );

  if (isTester) return (
    <div className="max-w-2xl mx-auto mt-16 card-surface p-8 text-center" data-testid="settings-restricted">
      <h2 className="font-heading text-2xl text-white mb-2">Settings are restricted</h2>
      <p className="text-sm text-[#A3B5C7]">The thinking model is managed by administrators. Contact an admin if a change is needed.</p>
    </div>
  );

  const isCalibrated = profile?.is_calibrated;

  return (
    <div className="max-w-4xl space-y-6" data-testid="cognitive-profile-page">
      <SettingsNav />

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white tracking-tight">Thinking Model</h1>
          <p className="text-sm text-[#A3B5C7] mt-1">
            Calibrated to how you think, decide, and communicate
          </p>
        </div>
        {isCalibrated && (
          <WriteOnly><button onClick={handleSave} disabled={saving} className="btn-primary text-sm flex items-center gap-2" data-testid="save-cognitive-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Profile
          </button></WriteOnly>
        )}
      </div>

      {/* Uncalibrated state */}
      {!isCalibrated && !scanDraft && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="card-surface p-8 text-center space-y-5">
          <div className="w-16 h-16 rounded-2xl bg-[#00D4C8]/10 flex items-center justify-center mx-auto">
            <Brain className="w-8 h-8 text-[#00D4C8]" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-white mb-2">Your Thinking Model has not been calibrated yet</h2>
            <p className="text-sm text-[#A3B5C7] max-w-lg mx-auto">
              Export your ChatGPT or Claude conversation history and paste it below. BREACH will scan it for your question patterns, communication style, and decision-making approach — and use that to personalise every output it generates for you permanently.
            </p>
            <p className="text-xs text-[#A3B5C7]/60 mt-2">This takes 2 minutes and makes every result BREACH produces sharper and more useful from day one.</p>
          </div>

          <div className="max-w-2xl mx-auto text-left space-y-3">
            <label className={labelCls}>Paste Conversation Export</label>
            <textarea
              className={inputCls + " h-48 font-mono text-xs"}
              value={scanText}
              onChange={e => setScanText(e.target.value)}
              placeholder="Paste your ChatGPT or Claude conversation export here..."
              data-testid="scan-textarea"
            />
            <div className="flex justify-end">
              <button
                onClick={handleScan}
                disabled={scanning || scanText.trim().length < 200}
                className="btn-primary text-sm flex items-center gap-2"
                data-testid="scan-btn"
              >
                {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {scanning ? 'Scanning...' : 'Scan & Generate Profile'}
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Scan draft review */}
      {scanDraft && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="card-surface p-6 border-2 border-[#00D4C8]/30 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-[#00D4C8]" />
              <h3 className="text-lg font-semibold text-white">Generated Profile Draft</h3>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setScanDraft(null)} className="btn-secondary text-xs px-3 py-1.5">Discard</button>
              <button onClick={applyDraft} className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5" data-testid="apply-draft-btn">
                <Zap className="w-3 h-3" /> Apply Draft
              </button>
            </div>
          </div>
          <div className="grid gap-3 text-sm">
            <div><span className="text-[#A3B5C7] text-xs uppercase tracking-wider">Information Processing</span><p className="text-[#D4E2F0] mt-1 whitespace-pre-wrap">{scanDraft.information_processing}</p></div>
            <div><span className="text-[#A3B5C7] text-xs uppercase tracking-wider">Decision Making</span><p className="text-[#D4E2F0] mt-1 whitespace-pre-wrap">{scanDraft.decision_making}</p></div>
            <div><span className="text-[#A3B5C7] text-xs uppercase tracking-wider">Communication Register</span><p className="text-[#D4E2F0] mt-1 whitespace-pre-wrap">{scanDraft.communication_register}</p></div>
            <div><span className="text-[#A3B5C7] text-xs uppercase tracking-wider">Core Operating Question</span><p className="text-[#D4E2F0] mt-1 italic">{scanDraft.core_operating_question}</p></div>
          </div>
        </motion.div>
      )}

      {/* Calibrated profile — editable sections */}
      {isCalibrated && (
        <>
          {profile?.updated_at && (
            <p className="text-[10px] text-[#A3B5C7]/60">
              Last updated: {new Date(profile.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </p>
          )}

          {/* Re-scan option */}
          <motion.div layout className="card-surface overflow-hidden">
            <div className="px-5 border-b border-[#1A2F4C]">
              <SectionHeader icon={Upload} title="Re-Calibrate" subtitle="Paste new conversation data to update your profile" collapsed={collapsed.scan} onToggle={() => toggle('scan')} />
            </div>
            {!collapsed.scan && (
              <div className="p-5 space-y-3">
                <textarea
                  className={inputCls + " h-32 font-mono text-xs"}
                  value={scanText}
                  onChange={e => setScanText(e.target.value)}
                  placeholder="Paste conversation export here to re-scan..."
                  data-testid="rescan-textarea"
                />
                <div className="flex justify-end">
                  <button onClick={handleScan} disabled={scanning || scanText.trim().length < 200} className="btn-primary text-xs flex items-center gap-2" data-testid="rescan-btn">
                    {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {scanning ? 'Scanning...' : 'Re-Scan & Update'}
                  </button>
                </div>
              </div>
            )}
          </motion.div>

          {/* Information Processing */}
          <motion.div layout className="card-surface overflow-hidden">
            <div className="px-5 border-b border-[#1A2F4C]">
              <SectionHeader icon={Brain} title="Information Processing Style" subtitle="How you absorb and structure information" collapsed={collapsed.info} onToggle={() => toggle('info')} />
            </div>
            {!collapsed.info && (
              <div className="p-5">
                <textarea className={inputCls + " h-40"} value={profile.information_processing || ''} onChange={e => update('information_processing', e.target.value)} data-testid="info-processing-input" />
              </div>
            )}
          </motion.div>

          {/* Decision Making */}
          <motion.div layout className="card-surface overflow-hidden">
            <div className="px-5 border-b border-[#1A2F4C]">
              <SectionHeader icon={Zap} title="Decision-Making Style" subtitle="How you evaluate options and commit" collapsed={collapsed.decision} onToggle={() => toggle('decision')} />
            </div>
            {!collapsed.decision && (
              <div className="p-5">
                <textarea className={inputCls + " h-40"} value={profile.decision_making || ''} onChange={e => update('decision_making', e.target.value)} data-testid="decision-making-input" />
              </div>
            )}
          </motion.div>

          {/* Communication */}
          <motion.div layout className="card-surface overflow-hidden">
            <div className="px-5 border-b border-[#1A2F4C]">
              <SectionHeader icon={MessageSquare} title="Communication Register" subtitle="Your natural voice and writing style" collapsed={collapsed.comm} onToggle={() => toggle('comm')} />
            </div>
            {!collapsed.comm && (
              <div className="p-5">
                <textarea className={inputCls + " h-40"} value={profile.communication_register || ''} onChange={e => update('communication_register', e.target.value)} data-testid="comm-register-input" />
              </div>
            )}
          </motion.div>

          {/* Priority Signals */}
          <motion.div layout className="card-surface overflow-hidden">
            <div className="px-5 border-b border-[#1A2F4C]">
              <SectionHeader icon={Target} title="Priority Signals" subtitle="What you act on and what you deprioritise" collapsed={collapsed.priority} onToggle={() => toggle('priority')} />
            </div>
            {!collapsed.priority && (
              <div className="p-5 space-y-5">
                <div>
                  <label className={labelCls + " text-emerald-400/80"}>Acts on immediately</label>
                  <ListEditor items={profile.priority_acts_on || []} onChange={v => update('priority_acts_on', v)} placeholder="e.g. Anything advancing the seed round" />
                </div>
                <div>
                  <label className={labelCls + " text-amber-400/80"}>Deprioritises</label>
                  <ListEditor items={profile.priority_deprioritises || []} onChange={v => update('priority_deprioritises', v)} placeholder="e.g. Tactical detail that can be delegated" />
                </div>
              </div>
            )}
          </motion.div>

          {/* Output Preferences */}
          <motion.div layout className="card-surface overflow-hidden">
            <div className="px-5 border-b border-[#1A2F4C]">
              <SectionHeader icon={Eye} title="Output Preferences" subtitle="How BREACH should structure each output type for you" collapsed={collapsed.output} onToggle={() => toggle('output')} />
            </div>
            {!collapsed.output && (
              <div className="p-5 space-y-4">
                {[
                  { key: 'research_reports', label: 'Research Reports' },
                  { key: 'outreach_drafts', label: 'Outreach Drafts' },
                  { key: 'weekly_summaries', label: 'Weekly Summaries' },
                  { key: 'meeting_briefs', label: 'Meeting Briefs' },
                  { key: 'recommendations', label: 'Recommendations' },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label className={labelCls}>{label}</label>
                    <input className={inputCls} value={(profile.output_preferences || {})[key] || ''} onChange={e => updatePref(key, e.target.value)} data-testid={`pref-${key}`} />
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Equity & Core Question */}
          <motion.div layout className="card-surface overflow-hidden">
            <div className="px-5 border-b border-[#1A2F4C]">
              <SectionHeader icon={Sparkles} title="Deeper Patterns" subtitle="Equity framing and core operating question" collapsed={collapsed.deeper} onToggle={() => toggle('deeper')} />
            </div>
            {!collapsed.deeper && (
              <div className="p-5 space-y-4">
                <div>
                  <label className={labelCls}>Equity & Partnership Framing</label>
                  <textarea className={inputCls + " h-24"} value={profile.equity_framing || ''} onChange={e => update('equity_framing', e.target.value)} data-testid="equity-framing-input" />
                </div>
                <div>
                  <label className={labelCls}>Core Operating Question</label>
                  <p className="text-[10px] text-[#A3B5C7]/60 mb-1">The single question underlying most of your requests</p>
                  <input className={inputCls} value={profile.core_operating_question || ''} onChange={e => update('core_operating_question', e.target.value)} data-testid="core-question-input" />
                </div>
              </div>
            )}
          </motion.div>

          {/* Bottom save */}
          <div className="flex justify-end pb-8">
            <button onClick={handleSave} disabled={saving} className="btn-primary text-sm flex items-center gap-2" data-testid="save-cognitive-btn-bottom">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Profile
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CognitiveProfilePage;
